import StyledInputErrorText from '@/components/StyledInputFields/StyledInputErrorText.tsx';
import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import StyledTextarea from '@/components/StyledInputFields/StyledTextarea.tsx';
import useUpdateAutomation from '@/hooks/api/automations/useUpdateAutomation.ts';
import {
  canUseCreatorNotificationMode,
  getRecipientOptions,
} from '@/pages/Editor/components/StepSidebar/automationNotificationSettingsUtils.ts';
import {
  Automation,
  AutomationNotificationEventSettings,
  AutomationNotificationRecipientMode,
  AutomationNotificationSettings,
  AutomationRunErroredNotificationSettings,
} from '@/types/automations.ts';
import { Checkbox, Stack } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';

type Props = {
  automation: Automation;
};

type NotificationEventKey = keyof AutomationNotificationSettings;

const NOTIFICATION_EVENTS: Array<{ key: NotificationEventKey; label: string; description?: string }> = [
  { key: 'run_started', label: 'Run started' },
  { key: 'run_completed', label: 'Run completed' },
  {
    key: 'run_errored',
    label: 'Run errors or needs attention',
    description: 'Recipients receive execution errors, ordinary runs requiring input, and completed runs whose declared outcome was not achieved, needs manual action, or could not be verified. Human Decision Gate reviewers use their own setting. Custom workflow emails remain separate unless the published version explicitly declares and verifies manual-handoff coverage.',
  },
  { key: 'human_decision_needed', label: 'Human decision needed' },
];

function getDefaultEventSettings(
  createdByType: Automation['created_by_type'],
): AutomationNotificationEventSettings {
  return {
    mode: canUseCreatorNotificationMode(createdByType) ? 'creator' : 'none',
    emails: [],
  };
}

function getDefaultHumanDecisionSettings(): AutomationNotificationEventSettings {
  return { mode: 'none', emails: [] };
}

function normalizeEventSettings(
  settings: AutomationNotificationEventSettings | undefined,
  createdByType: Automation['created_by_type'],
): AutomationNotificationEventSettings {
  if (!settings) {
    return getDefaultEventSettings(createdByType);
  }

  if (settings.mode === 'creator' && !canUseCreatorNotificationMode(createdByType)) {
    return getDefaultEventSettings(createdByType);
  }

  return settings;
}

function normalizeRunErroredSettings(
  settings: AutomationRunErroredNotificationSettings | undefined,
  createdByType: Automation['created_by_type'],
): AutomationRunErroredNotificationSettings {
  const normalized = normalizeEventSettings(settings, createdByType);
  return {
    ...normalized,
    recovery_policy: settings?.recovery_policy ?? 'always_send',
  };
}

function getNotificationSettings(automation: Automation): AutomationNotificationSettings {
  return {
    run_started: normalizeEventSettings(
      automation.notification_settings?.run_started,
      automation.created_by_type,
    ),
    run_completed: normalizeEventSettings(
      automation.notification_settings?.run_completed,
      automation.created_by_type,
    ),
    run_errored: normalizeRunErroredSettings(
      automation.notification_settings?.run_errored,
      automation.created_by_type,
    ),
    human_decision_needed: normalizeEventSettings(
      automation.notification_settings?.human_decision_needed ?? getDefaultHumanDecisionSettings(),
      automation.created_by_type,
    ),
  };
}

function makeEmailText(settings: AutomationNotificationSettings): Record<NotificationEventKey, string> {
  return {
    run_started: settings.run_started.emails.join('\n'),
    run_completed: settings.run_completed.emails.join('\n'),
    run_errored: settings.run_errored.emails.join('\n'),
    human_decision_needed: settings.human_decision_needed?.emails.join('\n') ?? '',
  };
}

function parseEmails(value: string): string[] {
  const seen = new Set<string>();
  const emails: string[] = [];

  value
    .split(/[,\n]+/)
    .map((email) => email.trim().toLowerCase())
    .filter(Boolean)
    .forEach((email) => {
      if (!seen.has(email)) {
        emails.push(email);
        seen.add(email);
      }
    });

  return emails;
}

const AutomationNotificationSettingsPanel = ({ automation }: Props) => {
  const { mutate: updateAutomation, isPending } = useUpdateAutomation();

  const initialSettings = useMemo(() => getNotificationSettings(automation), [automation]);

  const [settings, setSettings] = useState<AutomationNotificationSettings>(initialSettings);
  const [emailText, setEmailText] = useState<Record<NotificationEventKey, string>>(
    makeEmailText(initialSettings),
  );
  const [emptyCustomEvents, setEmptyCustomEvents] = useState<Set<NotificationEventKey>>(new Set());

  useEffect(() => {
    setSettings(initialSettings);
    setEmailText(makeEmailText(initialSettings));
    setEmptyCustomEvents(new Set());
  }, [initialSettings]);

  const allowCreatorNotifications = canUseCreatorNotificationMode(automation.created_by_type);
  const recipientOptions = getRecipientOptions(automation);

  const persistSettings = (nextSettings: AutomationNotificationSettings) => {
    updateAutomation({ id: automation.id, notification_settings: nextSettings });
  };

  const updateEventMode = (
    eventKey: NotificationEventKey,
    mode: AutomationNotificationRecipientMode,
  ) => {
    if (mode === 'creator' && !allowCreatorNotifications) return;

    const current = settings[eventKey] ?? getDefaultEventSettings(automation.created_by_type);
    const nextEventSettings: AutomationNotificationEventSettings =
      mode === 'custom'
        ? { mode, emails: current.emails }
        : { mode, emails: [] };
    const nextSettings = {
      ...settings,
      [eventKey]:
        eventKey === 'run_errored'
          ? { ...nextEventSettings, recovery_policy: settings.run_errored.recovery_policy }
          : nextEventSettings,
    } as AutomationNotificationSettings;

    setSettings(nextSettings);
    if (mode !== 'custom' || nextEventSettings.emails.length > 0) {
      setEmptyCustomEvents((events) => {
        const next = new Set(events);
        next.delete(eventKey);
        return next;
      });
      persistSettings(nextSettings);
    }
  };

  const updateCustomEmails = (eventKey: NotificationEventKey) => {
    const emails = parseEmails(emailText[eventKey]);
    if (emails.length === 0) {
      setEmptyCustomEvents((events) => new Set(events).add(eventKey));
      return;
    }

    setEmptyCustomEvents((events) => {
      const next = new Set(events);
      next.delete(eventKey);
      return next;
    });

    const nextSettings = {
      ...settings,
      [eventKey]:
        eventKey === 'run_errored'
          ? { mode: 'custom' as const, emails, recovery_policy: settings.run_errored.recovery_policy }
          : { mode: 'custom' as const, emails },
    } as AutomationNotificationSettings;
    setSettings(nextSettings);
    setEmailText((current) => ({ ...current, [eventKey]: emails.join('\n') }));
    persistSettings(nextSettings);
  };

  return (
    <Stack gap="sm">
      {NOTIFICATION_EVENTS.map(({ key, label, description }) => {
        const eventSettings = settings[key] ?? getDefaultEventSettings(automation.created_by_type);
        const isCustom = eventSettings.mode === 'custom';

        return (
          <Stack key={key} gap={6}>
            <StyledSelect
              label={label}
              description={description}
              data={recipientOptions}
              value={eventSettings.mode}
              onChange={(value) => {
                if (!value) return;
                updateEventMode(key, value as AutomationNotificationRecipientMode);
              }}
              disabled={isPending}
              allowDeselect={false}
              clearable={false}
            />
            {isCustom && (
              <>
                <StyledTextarea
                  value={emailText[key]}
                  onChange={(event) =>
                    setEmailText((current) => ({ ...current, [key]: event.currentTarget.value }))
                  }
                  onBlur={() => updateCustomEmails(key)}
                  disabled={isPending}
                  autosize
                  minRows={2}
                  maxRows={5}
                  placeholder="name@example.com, team@example.com"
                />
                {emptyCustomEvents.has(key) && (
                  <StyledInputErrorText>Enter at least one email address.</StyledInputErrorText>
                )}
              </>
            )}
            {key === 'run_errored' && eventSettings.mode !== 'none' && (
              <Checkbox
                checked={
                  settings.run_errored.recovery_policy ===
                  'suppress_after_successful_error_boundary_recovery'
                }
                disabled={isPending}
                label="Suppress generic run-error email after successful response steps"
                description="Successful response steps must perform required notifications or escalation. Applies to execution errors only; needs-input and declared-outcome notifications are unaffected. The generic email remains the fallback if recovery fails."
                onChange={(event) => {
                  const nextSettings: AutomationNotificationSettings = {
                    ...settings,
                    run_errored: {
                      ...settings.run_errored,
                      recovery_policy: event.currentTarget.checked
                        ? 'suppress_after_successful_error_boundary_recovery'
                        : 'always_send',
                    },
                  };
                  setSettings(nextSettings);
                  persistSettings(nextSettings);
                }}
              />
            )}
          </Stack>
        );
      })}
    </Stack>
  );
};

export default AutomationNotificationSettingsPanel;
